<template>
	<div class="dynamic-sidebar-wrapper">
		<!-- <span>{{ baseStore.allStates }}</span> -->
		<div class="dynamic-sidebar" :class="{ hide: hideSidebar }">
			<div class="divider">{{ t('layout.panel.graphs') }}</div>
			<ul class="rev-menu flex-col gap-2" v-if="isLocationRoute && projectId">
				<template v-for="(graph, graphKey) in baseStore.getGraphsByProjecId?.[projectId] || []" :key="graphKey">
					<li>
						<RouterLink
							class="rev-menu-item condensed rounded-lg"
							:class="{
								__selected: graph._id === baseStore.getActiveIds.moduleId,
							}"
							active-class="active"
							:to="`/modules/${graph._id}`">
							<span class="flex items-center gap-2 truncate">
								<StatusLight type="graph" :_id="graph._id" />
								<span class="truncate">
									{{ graph.name || graph._id }}
								</span>
							</span>
						</RouterLink>
					</li>
				</template>
				<li>
					<RouterLink class="rev-menu-item rounded-lg" active-class="active" :to="newGraphRoute">
						<span class="flex items-center gap-2 truncate">
							<span class="truncate">
								<PlusCircle class="icon-inline" />
								{{ t('layout.panel.newGraph') }}
							</span>
						</span>
					</RouterLink>
				</li>

				<template v-if="hasAnyModules">
					<div class="divider mt-12">{{ t('layout.panel.modules') }}</div>

					<!-- v-for="(location, locKey) in locationStore.locationsByProjectId[projectId]" -->
					<template
						v-for="(location, locKey) in baseStore.megaMap.projects[projectId].locations || {}"
						:key="`loc-${locKey}`">
					<li class="">
						<RouterLink
							:to="{ name: 'Location', params: { locationId: location.meta._id } }"
							class="rev-menu-item font-bold"
							:class="{
								__selected: location.meta._id === baseStore.getActiveIds.locationId,
							}">
							<StatusLight type="location" :_id="location.meta._id" />
							<span class="truncate font-bold">
								{{ location.meta.name }}
							</span>
						</RouterLink>

						<ul class="bg-base-200/0 mb-4 ml-4 rounded-lg">
							<template
								v-for="(graph, graphKey) in baseStore.getGraphsByLocationId?.[location.meta._id] || []"
								:key="graphKey">
								<li class="">
									<RouterLink
										class="rev-menu-item condensed rounded-lg"
										:class="{
											__selected: graph._id === baseStore.getActiveIds.moduleId,
										}"
										active-class="active"
										:to="`/modules/${graph._id}`">
										<span class="flex items-center gap-2 truncate">
											<StatusLight type="graph" :_id="graph._id" />
											<span class="truncate">
												{{ graph.name || graph._id }}
											</span>
										</span>
									</RouterLink>
								</li>
							</template>

							<!-- Hubs/Module nur für Admins und Entwickler; normale Nutzer sehen nur Graphen. -->
							<template v-if="isAdmin">
								<template v-for="(hub, hubKey) in location.hubs" :key="hubKey">
									<template v-for="(modul, modulKey) in hub.modules || {}" :key="modulKey">
										<li>
											<RouterLink
												class="rev-menu-item condensed rounded-lg"
												:class="{
													__selected: modul._id === baseStore.getActiveIds.moduleId,
												}"
												active-class="active"
												:to="{ name: 'Module', params: { moduleId: modul._id } }">
												<span class="flex items-center gap-2 truncate">
													<StatusLight type="module" :_id="modul._id" />
													<span class="truncate">
														{{ modul.customName || modul.name || modul.serial }}
													</span>
												</span>
											</RouterLink>
										</li>
									</template>
								</template>
							</template>
						</ul>
					</li>
					</template>
				</template>

				<!-- ComApps -->
				<template v-if="hasAnyComapps">
					<div class="divider mt-8">{{ t('layout.panel.comapps') }}</div>
					<template
						v-for="(location, locKey) in baseStore.megaMap.projects[projectId].locations || {}"
						:key="`comapp-loc-${locKey}`">
						<template
							v-for="(comapp, comappKey) in comappStore.comappsByLId[location.meta._id] || []"
							:key="`comapp-${comappKey}`">
							<li>
								<RouterLink
									class="rev-menu-item condensed rounded-lg"
									:class="{
										__selected: comapp._id === baseStore.getActiveIds.comappId,
									}"
									active-class="active"
									:to="`/comapp/${comapp.pcName}`">
									<span class="flex items-center gap-2 truncate">
										<StatusLight type="comapp" :_id="comapp._id" />
										<span class="truncate">
											{{ comapp.pcName }}
										</span>
									</span>
								</RouterLink>
							</li>
						</template>
					</template>
				</template>
				<li class="mb-24"></li>
			</ul>
		</div>
	</div>
</template>

<script lang="ts" setup>
	// import MegaNav from '@/components/layout/MegaNav.vue'
	import { PlusCircle } from 'lucide-vue-next'
	import { useRoute } from 'vue-router'
	import { computed, ref } from 'vue'
	import StatusLight from '@/components/StatusLight.vue'
	import { useAuthStore } from '@/stores/auth.store'
	import { useBaseStore } from '@/stores/base.store'
	import { useComappStore } from '@/stores/comapps.store'
	import { useI18n } from 'vue-i18n'
	import { _translations } from './_translations'

	// import { useElementSi
	const route = useRoute()
	const authStore = useAuthStore()
	const baseStore = useBaseStore()
	const comappStore = useComappStore()
	const { t } = useI18n(_translations)

	const isAdmin = computed(() => ['admin', 'developer'].includes(authStore.role || ''))

	const hideHubs = ref(false)

	const projectId = computed<string | null>(() => baseStore.getActiveIds.projectId)

	// Pre-fill the new-graph form with the project + location the user is
	// currently looking at — route params get dropped on navigation, so we
	// pass them as query params and the cgSettings store reads them.
	const newGraphRoute = computed(() => {
		const query: Record<string, string> = {}
		const locationId = baseStore.getActiveIds.locationId
		const pid = baseStore.getActiveIds.projectId
		if (locationId) query.locationId = locationId
		if (pid) query.projectId = pid
		return { path: '/newgraph', query }
	})

	const isLocationRoute = computed(() => {
		return ['Location', 'Hub', 'Module'].includes(route.name as string)
	})

	const hideSidebar = computed(() => !baseStore.getActiveIds._showPanel)

	const hasAnyModules = computed(() => {
		if (!projectId.value) return false
		const locations = baseStore.megaMap.projects[projectId.value]?.locations || {}
		return Object.values(locations).some((loc: any) => {
			const hasGraphs = (baseStore.getGraphsByLocationId?.[loc.meta._id] || []).length > 0
			// Module zählen nur für Admins/Devs — normale Nutzer sehen sie nicht,
			// also soll eine Nur-Module-Location die Sektion nicht aufklappen.
			const hasModules = isAdmin.value && Object.values(loc.hubs || {}).some((hub: any) =>
				Object.keys(hub.modules || {}).length > 0
			)
			return hasGraphs || hasModules
		})
	})

	const hasAnyComapps = computed(() => {
		if (!projectId.value) return false
		const locations = baseStore.megaMap.projects[projectId.value]?.locations || {}
		return Object.values(locations).some((loc: any) =>
			(comappStore.comappsByLId[loc.meta._id] || []).length > 0
		)
	})
</script>

<style scoped>
	@reference '@/styles/tailwind.css';

	.dynamic-sidebar-wrapper {
		@apply sticky top-16;
		/* @apply overflow-hidden; */
		@apply bg-base-100;
		@apply z-99;
		/* @apply w-[var(--layout-dynamic-sidebar-width)]; */

		.dynamic-sidebar {
			/* @apply w-full; */
			@apply overflow-auto;
			/* @apply bg-amber-100; */
			@apply z-100;
			@apply opacity-100;
			@apply w-[var(--layout-dynamic-sidebar-width)];
			@apply transition-all duration-50 ease-in-out;
			@apply shadow-lg;
			&.hide {
				@apply opacity-0;
				@apply w-0;
			}
			.__selected {
				@apply text-base-content;
				@apply bg-primary/50;
				/* @apply ring-primary ring; */
			}
		}
	}
</style>
